import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  sendCustomerStatement,
  type CustomerStatement,
  type CustomerStatementDispatch,
  type CustomerStatementMode,
} from '@/services/financialReports.service';
import SendReportModal from '@/components/finance/report-document/SendReportModal';

interface Props {
  open: boolean;
  statement: CustomerStatement;
  mode: CustomerStatementMode;
  currency?: string;
  balanceLabel: string;
  overdueLabel: string;
  onPreview: () => void;
  onClose: () => void;
  onSent: (dispatch: CustomerStatementDispatch) => void;
}

function defaultMessage(s: CustomerStatement, balanceLabel: string, overdueLabel: string): string {
  const d = (iso: string) => dayjs(iso).format('DD-MM-YYYY');
  const lines = [
    `Please find attached your statement of account ${s.statement_ref} for the period ${d(s.period.from)} to ${d(s.period.to)}.`,
    `The balance due as at ${d(s.period.to)} is ${balanceLabel}.`,
  ];
  if (s.summary.overdue > 0.005) {
    lines.push(`Of this, ${overdueLabel} is past due. We would appreciate settlement at your earliest convenience, or an update on the expected payment date.`);
  } else {
    lines.push('Thank you for keeping your account in good standing.');
  }
  return lines.join('\n');
}

const SendStatementModal: React.FC<Props> = ({ open, statement, mode, currency, balanceLabel, overdueLabel, onPreview, onClose, onSent }) => {
  const defaults = useMemo(
    () => ({
      to: statement.customer.email ? [statement.customer.email] : [],
      cc: [],
      subject: `Statement of Account ${statement.statement_ref} - ${statement.customer.customer_name}`,
      message: defaultMessage(statement, balanceLabel, overdueLabel),
    }),
    [statement, balanceLabel, overdueLabel]
  );

  return (
    <SendReportModal
      open={open}
      title="Email statement to customer"
      okText="Send statement"
      defaults={defaults}
      attachmentLabel={`The letterhead PDF (${mode === 'open_items' ? 'open items' : 'activity and open items'}) will be attached.`}
      recipientHint={!statement.customer.email ? 'No email on the customer master - add one here.' : undefined}
      onPreview={onPreview}
      onClose={onClose}
      onSubmit={async (v) => {
        const dispatch = await sendCustomerStatement({
          customer_id: statement.customer.id,
          from_date: statement.period.from,
          to_date: statement.period.to,
          currency,
          mode,
          to: v.to,
          cc: v.cc,
          subject: v.subject,
          message: v.message,
        });
        onSent(dispatch);
        return dispatch;
      }}
    />
  );
};

export default SendStatementModal;
